import { ApiError, api, deviceId, hasSession } from '../api/client.js'
import {
  db,
  getMeta,
  type LocalAward,
  type LocalBarcode,
  type LocalBodyMeasurement,
  type LocalFood,
  type LocalGoal,
  type LocalRecipe,
  type LocalWorkout,
  searchKeyFor,
  setMeta,
} from '../db/dexie.js'
import { pruneFoodCache } from '../db/repo.js'
import { announceScaleReading } from '../glitime/scale.js'

/**
 * Delta sync.
 *
 * Push first, then pull: sending local edits before asking for remote ones
 * means the server's last-write-wins comparison sees this device's newest state
 * and the pull that follows already reflects the merge.
 *
 * iOS has no Background Sync API, so sync is driven entirely by foreground
 * events — app open, tab focus, returning online, and after a mutation.
 */

export type SyncStatus = 'idle' | 'syncing' | 'offline' | 'error'

export interface SyncState {
  status: SyncStatus
  lastSyncedAt: string | null
  pendingCount: number
  error: string | null
}

type Listener = (state: SyncState) => void

const nowIso = () => new Date().toISOString()

let state: SyncState = { status: 'idle', lastSyncedAt: null, pendingCount: 0, error: null }
const listeners = new Set<Listener>()
let running = false
let queuedRun = false

function emit(patch: Partial<SyncState>): void {
  state = { ...state, ...patch }
  for (const l of listeners) l(state)
}

export function subscribeSync(listener: Listener): () => void {
  listeners.add(listener)
  listener(state)
  return () => listeners.delete(listener)
}

export function getSyncState(): SyncState {
  return state
}

export async function countPending(): Promise<number> {
  const counts = await Promise.all([
    db.logEntries.where('dirty').equals(1).count(),
    db.foods.where('dirty').equals(1).count(),
    db.barcodes.where('dirty').equals(1).count(),
    db.recipes.where('dirty').equals(1).count(),
    db.goals.where('dirty').equals(1).count(),
    db.wearableDaily.where('dirty').equals(1).count(),
    db.bodyMeasurements.where('dirty').equals(1).count(),
    db.workouts.where('dirty').equals(1).count(),
    db.awards.where('dirty').equals(1).count(),
  ])
  return counts.reduce((total, count) => total + count, 0)
}

const strip = <T extends { dirty: unknown }>(row: T) => {
  const { dirty: _dirty, ...rest } = row
  return rest
}

const stripFood = (row: LocalFood) => {
  const { dirty: _dirty, cachedAt: _cachedAt, pinned: _pinned, searchKey: _key, ...rest } = row
  return rest
}

const stripBarcode = (row: LocalBarcode) => {
  const { dirty: _dirty, scannedAt: _scannedAt, boundAt: _boundAt, ...rest } = row
  return rest
}

/**
 * The most rows of each table one push may carry — `syncPushBodySchema`'s own
 * caps, mirrored here.
 *
 * Exceeding one does not make a large request, it makes a rejected one: the
 * schema refuses the whole body, every retry fails the same way, and since push
 * runs before pull, nothing moves in either direction ever again. A few weeks
 * offline is enough to reach that state, and water is logged a glass at a time.
 */
const PUSH_LIMITS = {
  logEntries: 500,
  foods: 200,
  barcodes: 200,
  recipes: 200,
  goals: 50,
  wearableDaily: 90,
  bodyMeasurements: 365,
  workouts: 500,
  awards: 200,
} as const

/** One row the server merged into another it already had. */
interface Remap {
  id: string
  canonicalId: string
}

interface PushResponse {
  /** Every pushed binding, flagged when the server bound the code elsewhere. */
  barcodes?: Array<{ code: string; foodId: string; remapped: boolean }>
  goalRemaps?: Remap[]
  bodyMeasurementRemaps?: Remap[]
  workoutRemaps?: Remap[]
  awardRemaps?: Remap[]
}

async function push(): Promise<void> {
  const [
    logEntries,
    foods,
    barcodes,
    recipes,
    goals,
    wearableDaily,
    bodyMeasurements,
    workouts,
    awards,
  ] = await Promise.all([
    db.logEntries.where('dirty').equals(1).toArray(),
    db.foods.where('dirty').equals(1).toArray(),
    db.barcodes.where('dirty').equals(1).toArray(),
    db.recipes.where('dirty').equals(1).toArray(),
    db.goals.where('dirty').equals(1).toArray(),
    db.wearableDaily.where('dirty').equals(1).toArray(),
    db.bodyMeasurements.where('dirty').equals(1).toArray(),
    db.workouts.where('dirty').equals(1).toArray(),
    db.awards.where('dirty').equals(1).toArray(),
  ])

  const sent = {
    logEntries: 0,
    foods: 0,
    barcodes: 0,
    recipes: 0,
    goals: 0,
    wearableDaily: 0,
    bodyMeasurements: 0,
    workouts: 0,
    awards: 0,
  }

  for (;;) {
    const batch = {
      logEntries: logEntries.slice(sent.logEntries, sent.logEntries + PUSH_LIMITS.logEntries),
      foods: foods.slice(sent.foods, sent.foods + PUSH_LIMITS.foods),
      barcodes: barcodes.slice(sent.barcodes, sent.barcodes + PUSH_LIMITS.barcodes),
      recipes: recipes.slice(sent.recipes, sent.recipes + PUSH_LIMITS.recipes),
      goals: goals.slice(sent.goals, sent.goals + PUSH_LIMITS.goals),
      wearableDaily: wearableDaily.slice(
        sent.wearableDaily,
        sent.wearableDaily + PUSH_LIMITS.wearableDaily,
      ),
      bodyMeasurements: bodyMeasurements.slice(
        sent.bodyMeasurements,
        sent.bodyMeasurements + PUSH_LIMITS.bodyMeasurements,
      ),
      workouts: workouts.slice(sent.workouts, sent.workouts + PUSH_LIMITS.workouts),
      awards: awards.slice(sent.awards, sent.awards + PUSH_LIMITS.awards),
    }
    const count = Object.values(batch).reduce((total, rows) => total + rows.length, 0)
    if (count === 0) return

    const response = await api.post<PushResponse>('/api/sync/push', {
      deviceId: await deviceId(),
      logEntries: batch.logEntries.map(strip),
      // `cachedAt` and `pinned` are how this device decides what to keep; the
      // server has no column for either and no opinion about them.
      foods: batch.foods.map(stripFood),
      barcodes: batch.barcodes.map(stripBarcode),
      recipes: batch.recipes.map(strip),
      goals: batch.goals.map(strip),
      wearableDaily: batch.wearableDaily.map(strip),
      bodyMeasurements: batch.bodyMeasurements.map(strip),
      workouts: batch.workouts.map(strip),
      awards: batch.awards.map(strip),
    })

    await db.transaction(
      'rw',
      [
        db.logEntries,
        db.foods,
        db.barcodes,
        db.recipes,
        db.goals,
        db.wearableDaily,
        db.bodyMeasurements,
        db.workouts,
        db.awards,
      ],
      async () => {
        // Only clear rows whose clientEditedAt still matches what was sent: an
        // edit made while the request was in flight must stay pending.
        const clear = async (
          table: {
            get: (k: string) => Promise<{ clientEditedAt?: string } | undefined>
            put: (v: unknown) => unknown
          },
          rows: Array<{ clientEditedAt?: string }>,
          keyOf: (r: never) => string,
        ) => {
          for (const row of rows) {
            const key = keyOf(row as never)
            const current = await table.get(key)
            if (current && current.clientEditedAt === row.clientEditedAt) {
              await table.put({ ...current, dirty: 0 })
            }
          }
        }
        await clear(db.logEntries as never, batch.logEntries, (r: { id: string }) => r.id)
        await clear(db.recipes as never, batch.recipes, (r: { id: string }) => r.id)
        await clear(db.goals as never, batch.goals, (r: { id: string }) => r.id)
        await clear(
          db.bodyMeasurements as never,
          batch.bodyMeasurements,
          (r: { id: string }) => r.id,
        )
        await clear(db.workouts as never, batch.workouts, (r: { id: string }) => r.id)
        await clear(db.awards as never, batch.awards, (r: { id: string }) => r.id)
        for (const f of batch.foods) await db.foods.update(f.id, { dirty: 0 })
        for (const b of batch.barcodes) await db.barcodes.update(b.code, { dirty: 0 })
        for (const w of batch.wearableDaily) {
          await db.wearableDaily.update(w.summaryDate, { dirty: 0 })
        }
      },
    )

    await applyServerDecisions(response)

    for (const key of Object.keys(sent) as Array<keyof typeof sent>) {
      sent[key] += batch[key].length
    }
  }
}

/**
 * What the server decided about rows two devices created for one thing.
 *
 * Read from `barcodes`, which is what the push route actually returns — the
 * field this used to look for never existed, so a device that lost a barcode
 * conflict quietly kept scanning the product into its own duplicate food. The
 * remap lists are consumed for the same reason: the loser row is the server's
 * canonical row under another id, and leaving it behind lets a stale copy win
 * a tie later.
 */
async function applyServerDecisions(response: PushResponse | undefined): Promise<void> {
  for (const binding of response?.barcodes ?? []) {
    if (!binding.remapped) continue
    const local = await db.barcodes.get(binding.code)
    if (local && local.foodId !== binding.foodId) {
      await db.barcodes.put({ ...local, foodId: binding.foodId, dirty: 0 })
    }
  }

  const dropLosers = async (
    table: {
      get: (id: string) => Promise<{ dirty: 0 | 1 } | undefined>
      delete: (id: string) => Promise<void>
    },
    remaps: Remap[] | undefined,
  ) => {
    for (const remap of remaps ?? []) {
      if (remap.id === remap.canonicalId) continue
      // An edit made since the push is not a duplicate yet; it goes up next run.
      const local = await table.get(remap.id)
      if (local && local.dirty === 0) await table.delete(remap.id)
    }
  }
  await dropLosers(db.goals as never, response?.goalRemaps)
  await dropLosers(db.bodyMeasurements as never, response?.bodyMeasurementRemaps)
  await dropLosers(db.workouts as never, response?.workoutRemaps)
  await dropLosers(db.awards as never, response?.awardRemaps)
}

interface PullPage {
  cursor: number
  hasMore: boolean
  foods?: unknown[]
  barcodes?: unknown[]
  logEntries?: unknown[]
  recipes?: unknown[]
  goals?: unknown[]
  wearableDaily?: unknown[]
  bodyMeasurements?: unknown[]
  workouts?: unknown[]
  awards?: unknown[]
}

/**
 * Collapse the rows two devices created for one thing.
 *
 * A weigh-in is identified by its morning and an imported workout by its
 * exporter and that exporter's id, so two phones offline on the same day invent
 * two row ids for one measurement. The server merges them onto whichever it saw
 * first and sends that back; without dropping the loser here the chart shows
 * that morning twice forever. Dirty rows are left alone — an unsent edit is not
 * a duplicate until the server has had its say.
 */
async function collapseDuplicates<T extends { id: string; deletedAt: string | null; dirty: 0 | 1 }>(
  incoming: unknown[] | undefined,
  /** Every live local row the server would treat as the same row as this one. */
  clashesWith: (row: T) => Promise<T[]>,
  drop: (ids: string[]) => Promise<void>,
): Promise<void> {
  for (const raw of incoming ?? []) {
    const row = raw as T
    if (row.deletedAt != null) continue
    const losers = (await clashesWith(row))
      .filter((c) => c.id !== row.id && c.dirty === 0 && c.deletedAt == null)
      .map((c) => c.id)
    if (losers.length > 0) await drop(losers)
  }
}

async function pull(): Promise<void> {
  let cursor = await getMeta<number>('syncCursor', 0)
  for (let page = 0; page < 50; page++) {
    const res = await api.get<PullPage>(`/api/sync/pull?cursor=${cursor}&limit=500`)
    const fromScale: LocalBodyMeasurement[] = []

    await db.transaction(
      'rw',
      [
        db.logEntries,
        db.foods,
        db.barcodes,
        db.recipes,
        db.goals,
        db.wearableDaily,
        db.bodyMeasurements,
        db.workouts,
        db.awards,
      ],
      async () => {
        // A row still dirty locally has an unsent edit that must not be clobbered
        // by the server's older copy; it wins here and is resolved on next push.
        const merge = async <T extends { dirty: 0 | 1 }>(
          table: { get: (k: string) => Promise<T | undefined>; put: (v: T) => unknown },
          rows: unknown[] | undefined,
          keyOf: (r: never) => string,
          extra: (r: never, local: T | undefined) => Partial<T> = () => ({}),
        ) => {
          for (const raw of rows ?? []) {
            const key = keyOf(raw as never)
            const local = await table.get(key)
            if (local?.dirty === 1) continue
            await table.put({ ...(raw as T), ...extra(raw as never, local), dirty: 0 })
          }
        }

        await merge(
          db.foods as never,
          res.foods,
          (r: { id: string }) => r.id,
          (r: never, local: LocalFood | undefined) => {
            const food = r as Pick<LocalFood, 'brand' | 'nameLt' | 'nameEn' | 'visibility'>
            // A private food only ever reaches the account that created it, so
            // it is the user's own: no expiry clock, never evicted. A public one
            // arrived because their diary or a recipe points at it, and is cache
            // like everything else the phone chose to keep — the first sighting
            // starts its clock, and a re-send must not restart it.
            const own = food.visibility === 'private'
            // A stored null is a decision rather than an absence: it marks a row
            // the user made here, which `saveFood` can do at public visibility
            // too. Reading it as missing would start a clock on their own
            // product and evict it out from under them ninety days later.
            const clock = local === undefined ? nowIso() : local.cachedAt
            return {
              searchKey: searchKeyFor(food.brand, food.nameLt, food.nameEn),
              cachedAt: own ? null : clock,
              pinned: own ? 1 : (local?.pinned ?? 0),
            } as never
          },
        )
        await merge(
          db.barcodes as never,
          res.barcodes,
          (r: { code: string }) => r.code,
          (_r: never, local: LocalBarcode | undefined) =>
            ({
              // Sync handing over a binding is not a scan. Keeping the stamp
              // null is what stops a catalog-wide barcode feed pinning every
              // food behind it and undoing the whole eviction pass.
              boundAt: local?.boundAt ?? nowIso(),
              scannedAt: local?.scannedAt ?? null,
            }) as never,
        )
        await merge(db.logEntries as never, res.logEntries, (r: { id: string }) => r.id)
        await merge(
          db.recipes as never,
          res.recipes,
          (r: { id: string }) => r.id,
          // A server that still ships retired ingredients inside the recipe
          // must not be able to put them back on this phone: the list is
          // replaced wholesale, so a tombstone in it reads as an ingredient.
          (r: never) =>
            ({
              ingredients: (r as LocalRecipe).ingredients.filter((i) => i.deletedAt == null),
            }) as never,
        )
        await merge(db.goals as never, res.goals, (r: { id: string }) => r.id)
        await merge(
          db.wearableDaily as never,
          res.wearableDaily,
          (r: { summaryDate: string }) => r.summaryDate,
        )
        await merge(
          db.bodyMeasurements as never,
          res.bodyMeasurements,
          (r: { id: string }) => r.id,
          // A scale row this phone has not seen in this shape is news worth a
          // word from her — collected here, spoken once the write has landed.
          (r: never, local: LocalBodyMeasurement | undefined) => {
            const row = r as LocalBodyMeasurement
            if (row.source === 'scale' && row.clientEditedAt !== local?.clientEditedAt) {
              fromScale.push(row)
            }
            return {} as never
          },
        )
        await merge(db.workouts as never, res.workouts, (r: { id: string }) => r.id)
        await merge(db.awards as never, res.awards, (r: { id: string }) => r.id)

        await collapseDuplicates<LocalBodyMeasurement>(
          res.bodyMeasurements,
          (row) => db.bodyMeasurements.where('measuredOn').equals(row.measuredOn).toArray(),
          (ids) => db.bodyMeasurements.bulkDelete(ids),
        )
        // A goal is identified by the day it starts — that is the key the server
        // merges on. Every save mints a fresh local id, so without this the
        // table fills with rows for one date and `goalForDate` picks between
        // them by whichever uuid happens to sort first: an old target could win
        // on one phone and stay there.
        await collapseDuplicates<LocalGoal>(
          res.goals,
          (row) => db.goals.where('effectiveFrom').equals(row.effectiveFrom).toArray(),
          (ids) => db.goals.bulkDelete(ids),
        )
        await collapseDuplicates<LocalWorkout>(
          res.workouts,
          // The source is part of the match because it is part of
          // `workouts_external_uq`. Two exporters both numbering their sessions
          // from one are not one session, and what gets dropped here is a row
          // the server still holds.
          async (row) =>
            row.externalId == null
              ? []
              : (await db.workouts.where('externalId').equals(row.externalId).toArray()).filter(
                  (w) => w.source === row.source,
                ),
          (ids) => db.workouts.bulkDelete(ids),
        )
        // An award's natural key is (userId, awardKey) — implicit here since
        // this device only ever sees its own rows. Two offline devices that
        // both detected the same trophy independently invent two ids for it;
        // the server keeps whichever it saw first and this drops the other.
        await collapseDuplicates<LocalAward>(
          res.awards,
          (row) => db.awards.where('awardKey').equals(row.awardKey).toArray(),
          (ids) => db.awards.bulkDelete(ids),
        )
      },
    )

    // After the transaction, so a listener that reads the table sees the row.
    for (const row of fromScale) announceScaleReading(row)

    cursor = res.cursor
    await setMeta('syncCursor', cursor)
    if (!res.hasMore) break
  }
}

/**
 * The catalog no longer arrives whole, so what the phone keeps has to be
 * trimmed by the phone. Once a day is plenty — neither the ninety-day clock nor
 * the row cap moves fast enough to justify walking the diary every five
 * minutes, which is how often sync itself runs.
 */
const PRUNE_INTERVAL_MS = 24 * 60 * 60 * 1000

async function pruneIfDue(): Promise<void> {
  const last = await getMeta<number>('lastPrunedAt', 0)
  if (Date.now() - last < PRUNE_INTERVAL_MS) return
  await pruneFoodCache()
  await setMeta('lastPrunedAt', Date.now())
}

/**
 * Run one sync cycle. Concurrent calls collapse into a single trailing run so
 * a burst of edits does not produce a burst of requests.
 */
export async function syncNow(): Promise<void> {
  if (!hasSession()) return
  if (!navigator.onLine) {
    emit({ status: 'offline', pendingCount: await countPending() })
    return
  }
  if (running) {
    queuedRun = true
    return
  }

  running = true
  emit({ status: 'syncing', error: null })
  try {
    // A push that cannot succeed must not take the pull down with it. The pull
    // is how a phone repairs itself: a row the server refuses is a row whose
    // corrected copy is sitting on the server waiting to come down, and while
    // the push threw first, nothing came down at all — one unsendable row and
    // the phone had stopped syncing in both directions, permanently. Pulling
    // after a failed push loses nothing, because the merge skips any row still
    // marked dirty.
    let pushFailed: unknown = null
    try {
      await push()
    } catch (err) {
      pushFailed = err
    }
    await pull()
    await pruneIfDue()
    if (pushFailed) throw pushFailed
    emit({
      status: 'idle',
      lastSyncedAt: nowIso(),
      pendingCount: await countPending(),
      error: null,
    })
    await setMeta('lastSyncedAt', nowIso())
  } catch (err) {
    const message = err instanceof ApiError ? err.message : 'Sinchronizavimas nepavyko'
    emit({
      status: navigator.onLine ? 'error' : 'offline',
      error: message,
      pendingCount: await countPending(),
    })
  } finally {
    running = false
    if (queuedRun) {
      queuedRun = false
      void syncNow()
    }
  }
}

/**
 * Wire sync to the only signals iOS actually gives an installed web app.
 * Returns a teardown function.
 */
export function startSyncTriggers(): () => void {
  const onVisible = () => {
    if (document.visibilityState === 'visible') void syncNow()
  }
  const onOnline = () => void syncNow()
  const onOffline = () => emit({ status: 'offline' })

  document.addEventListener('visibilitychange', onVisible)
  window.addEventListener('online', onOnline)
  window.addEventListener('offline', onOffline)
  const interval = window.setInterval(() => void syncNow(), 5 * 60 * 1000)
  void syncNow()

  return () => {
    document.removeEventListener('visibilitychange', onVisible)
    window.removeEventListener('online', onOnline)
    window.removeEventListener('offline', onOffline)
    window.clearInterval(interval)
  }
}
